import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { DeleteDialog } from 'common/components/deleteDialog/DeleteDialog';
import { DeleteDialogCustomField } from 'common/components/deleteDialog/DeleteDialogCustomField';
import { setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { selectPageInfo, selectPaginationGridData, setPaginationGridData } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { deleteTenantUserMembership, fetchTenantUserPaginatedMemberships } from 'common/services/api/TenantUserMembership/TenantUserMembership.thunk';
import { DeleteDialogContentProps } from 'common/types/TypeDefs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { selectGlobalRoles } from 'features/globalRoles/globalRolesSlice';
import { useSnackbar } from 'notistack';

import { selectFilters } from './tenantUserMembershipSlice';

export function TenantUserMembershipDeleteDialogContent({
    title,
    itemId,
    editingRow,
    paginationInfo
}: Readonly<DeleteDialogContentProps<{ firstName: string, lastName: string, userId: string }>>) {
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const filterProp = useAppSelector(selectFilters);
    const { firstName, lastName, userId } = editingRow ?? {};
    const { rowsOnPage } = paginationInfo ?? {};
    const { currentPage = 0, rowsPerPage = 0, sortModel } = paginationGridData ?? {};

    const { keywords, dropdowns } = filterProp;
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const userName = `${firstName} ${lastName}`;

    const successDeleteHandler = (): void => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('User has been removed from this Tenant.', { variant: 'success' });
        setProcessing(false);
    };

    const dispatch = useAppDispatch();
    const params = useParams();
    const { tenantId } = params;
    const pageInfo = useAppSelector(selectPageInfo);
    const globalRoles = useAppSelector(selectGlobalRoles);

    const tenantUserDeleteCallback = useCallback(() => {
        const filteredRoles = globalRoles.filter(role => dropdowns.globalRoles.keys.includes(role.name));
        if (tenantId && userId) {
            setProcessing(true);
            dispatch(deleteTenantUserMembership({
                tenantId,
                userId,
                skip: (rowsOnPage === 1) && (currentPage > 0) ? ((currentPage - 1) * rowsPerPage) : (currentPage * rowsPerPage),
                take: rowsPerPage,
                search: keywords[0],
                globalRoles: filteredRoles,
                status: dropdowns.status.keys
            }))
                .unwrap()
                .then((result) => {
                    dispatch(setPaginationGridData({
                        rows: result.rows,
                        rowsCount: result.count,
                        currentPage: result.skipped / rowsPerPage,
                        rowsPerPage,
                        sortModel,
                    }));
                    successDeleteHandler();
                })
                .catch(error => {
                    const statusCode = extractStatusCode(error.message);
                    if (statusCode === 409) {
                        enqueueSnackbar(
                            'The User needs to be removed from all User Groups, Roles, Claims, and ACL Entries prior to removal from this Tenant.',
                            { variant: 'error' }
                        );
                        setProcessing(false);
                        return;
                    }
                    if (statusCode !== 404) {
                        enqueueSnackbar(
                            'There was an error while performing this action.' +
                        DEFAULT_ERROR_MESSAGE,
                            { variant: 'error' }
                        );
                        return;
                    }
                    if (currentPage === 0) {
                        dispatch(setPaginationGridData({}));
                        successDeleteHandler();
                        return;
                    }
                    dispatch(fetchTenantUserPaginatedMemberships({
                        tenantId: tenantId ?? '',
                        skip: 0,
                        take: rowsPerPage,
                        search: keywords[0],
                        globalRoles: filteredRoles,
                        status: dropdowns.status.keys,
                        sortKey: sortModel?.[0]?.field,
                        sortOrder: sortModel?.[0]?.sort,
                    }))
                        .unwrap()
                        .then((result) => {
                            dispatch(setPaginationGridData({
                                rows: result.data,
                                rowsCount: result.count,
                                currentPage: result.skipped / rowsPerPage,
                                rowsPerPage,
                                sortModel
                            }));
                        })
                        .catch(() => {
                            dispatch(setPaginationGridData({}));
                        })
                        .finally(() => {
                            successDeleteHandler();
                        });
                })
                .finally(() => {
                    setProcessing(false);
                });
        }
    }, [itemId]);

    return  (
        <DeleteDialog deleteCallback={tenantUserDeleteCallback} processing={processing ?? false} title={`${title} from this Tenant`}>
            <DeleteDialogCustomField fieldName='User' fieldValue={userName}/>
            <DeleteDialogCustomField fieldName='Tenant Name' fieldValue={pageInfo?.name}/>
        </DeleteDialog>
    );
}
